import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, Briefcase, Loader2 } from 'lucide-react';

export interface VendorWaitlistFormData {
  // Business Information
  businessName: string;
  contactName: string;
  jobTitle: string;
  email: string;
  phone: string;

  // Service Details
  serviceCategory: string;
  serviceDescription: string;

  // Business Details
  yearsInBusiness: string;
  preferredPlan: string;

  // Optional
  website?: string;
  additionalInfo?: string;
}

export interface VendorWaitlistFormProps {
  cityName: string;
  stateName?: string;
  vendorMonthlyFee?: string;
  vendorAnnualFee?: string;
  contactEmail?: string;
  /**
   * Required. Must actually persist the submission. There is deliberately no
   * default: a mock default that returned { success: true } showed residents a
   * fake confirmation on any tenant that forgot to pass a real handler.
   */
  onSubmit: (data: VendorWaitlistFormData) => Promise<{ success: boolean; error?: string }>;
  validateForm?: (data: VendorWaitlistFormData) => string | null;
}

/**
 * VendorWaitlistForm - Parameterized vendor waitlist form
 *
 * Multi-step form for vendors to join the waitlist for a specific city.
 * Accepts custom validation and submission handlers.
 *
 * @example
 * ```tsx
 * <VendorWaitlistForm
 *   cityName="Houston"
 *   stateName="Texas"
 *   vendorMonthlyFee="$25"
 *   vendorAnnualFee="$180"
 *   contactEmail="vendors@houston.community"
 *   onSubmit={handleVendorSubmit}
 * />
 * ```
 */
export function VendorWaitlistForm({
  cityName,
  stateName = '',
  vendorMonthlyFee = '$25',
  vendorAnnualFee = '$180',
  contactEmail = '',
  onSubmit,
  validateForm,
}: VendorWaitlistFormProps) {
  const [formData, setFormData] = useState<VendorWaitlistFormData>({
    businessName: '',
    contactName: '',
    jobTitle: '',
    email: '',
    phone: '',
    serviceCategory: '',
    serviceDescription: '',
    yearsInBusiness: '',
    preferredPlan: '',
    website: '',
    additionalInfo: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cityStateDisplay = stateName ? `${cityName}, ${stateName}` : cityName;

  const defaultValidation = (data: VendorWaitlistFormData): string | null => {
    if (!data.businessName.trim()) return 'Business name is required';
    if (!data.contactName.trim()) return 'Contact name is required';
    if (!data.jobTitle) return 'Job title is required';
    if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      return 'Valid email address is required';
    }
    if (!data.phone.trim()) return 'Phone number is required';
    if (!data.serviceCategory) return 'Service category is required';
    if (!data.serviceDescription.trim() || data.serviceDescription.length < 20) {
      return 'Service description must be at least 20 characters';
    }
    if (!data.yearsInBusiness) return 'Years in business is required';
    if (!data.preferredPlan) return 'Preferred plan is required';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    const validator = validateForm || defaultValidation;
    const validationError = validator(formData);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await onSubmit(formData);

      if (response.success) {
        setSubmitted(true);
      } else {
        setError(response.error || 'Submission failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="text-center border-green-200 bg-green-50/50">
          <CardHeader className="px-4 sm:px-6 pt-6 sm:pt-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl sm:text-3xl">You're on the Waitlist!</CardTitle>
            <CardDescription className="text-base sm:text-lg mt-2 text-foreground/80">
              You're now on the waitlist for {cityStateDisplay}. We'll notify you when the platform
              launches.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 px-4 sm:px-6 pb-6 sm:pb-8">
            <div className="bg-white border border-green-200 rounded-lg p-4 space-y-3 text-left">
              <h4 className="font-semibold text-green-900">What happens next?</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>We'll email you when the platform launches and onboarding opens</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>You'll complete enterprise-grade KYC/KYB verification to get approved</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Once verified, you'll get immediate access to the vendor marketplace</span>
                </li>
              </ul>
            </div>

            <p className="text-sm text-muted-foreground">
              {/* Questions? Email us at <a href={`mailto:${contactEmail}`} className="text-primary hover:underline font-medium">{contactEmail}</a> */}
            </p>

            <div className="flex gap-3 sm:gap-4 justify-center mt-6">
              <Button
                variant="outline"
                onClick={() => setSubmitted(false)}
                className="text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-2.5"
              >
                Submit Another Application
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader className="px-4 sm:px-6 md:px-8 pt-6 sm:pt-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl sm:text-3xl md:text-4xl text-center leading-tight">
            Join the Vendor Waitlist
          </CardTitle>
          <CardDescription className="text-center text-sm sm:text-base md:text-lg mt-3 sm:mt-4">
            Reserve your spot as a verified vendor in {cityStateDisplay}. You'll be notified when
            the platform launches and can begin the official onboarding process.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 md:px-8 pb-6 sm:pb-8">
          {/* Error Display */}
          {error && (
            <Alert variant="destructive" className="mb-4 sm:mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
            {/* Business Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Business Information</h3>

              <div className="space-y-2">
                <Label htmlFor="businessName" className="text-sm sm:text-base">
                  Business Name *
                </Label>
                <Input
                  id="businessName"
                  placeholder="Your business or company name"
                  required
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  className="text-sm sm:text-base h-10 sm:h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactName" className="text-sm sm:text-base">
                  Your Name *
                </Label>
                <Input
                  id="contactName"
                  placeholder="Contact person's full name"
                  required
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  className="text-sm sm:text-base h-10 sm:h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="jobTitle" className="text-sm sm:text-base">
                  Your Title in Company *
                </Label>
                <Select
                  value={formData.jobTitle}
                  onValueChange={(value) => setFormData({ ...formData, jobTitle: value })}
                >
                  <SelectTrigger id="jobTitle" className="text-sm sm:text-base h-10 sm:h-11">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="ceo">CEO</SelectItem>
                    <SelectItem value="president">President</SelectItem>
                    <SelectItem value="managing-director">Managing Director</SelectItem>
                    <SelectItem value="operations-manager">Operations Manager</SelectItem>
                    <SelectItem value="general-manager">General Manager</SelectItem>
                    <SelectItem value="business-development">
                      Business Development Manager
                    </SelectItem>
                    <SelectItem value="sales-manager">Sales Manager</SelectItem>
                    <SelectItem value="account-manager">Account Manager</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm sm:text-base">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@business.com"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="text-sm sm:text-base h-10 sm:h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm sm:text-base">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 555-1234"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="text-sm sm:text-base h-10 sm:h-11"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website" className="text-sm sm:text-base">
                  Website (Optional)
                </Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://yourbusiness.com"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="text-sm sm:text-base h-10 sm:h-11"
                />
              </div>
            </div>

            {/* Service Details Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Service Details</h3>

              <div className="space-y-2">
                <Label htmlFor="serviceCategory" className="text-sm sm:text-base">
                  Service Category *
                </Label>
                <Select
                  value={formData.serviceCategory}
                  onValueChange={(value) => setFormData({ ...formData, serviceCategory: value })}
                >
                  <SelectTrigger id="serviceCategory" className="text-sm sm:text-base h-10 sm:h-11">
                    <SelectValue placeholder="Select your primary service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lawn-care">Lawn Care & Landscaping</SelectItem>
                    <SelectItem value="snow-removal">Snow Removal</SelectItem>
                    <SelectItem value="hvac">HVAC Services</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="electrical">Electrical Services</SelectItem>
                    <SelectItem value="roofing">Roofing</SelectItem>
                    <SelectItem value="painting">Painting</SelectItem>
                    <SelectItem value="cleaning">Cleaning Services</SelectItem>
                    <SelectItem value="pest-control">Pest Control</SelectItem>
                    <SelectItem value="handyman">Handyman Services</SelectItem>
                    <SelectItem value="security">Security Systems</SelectItem>
                    <SelectItem value="pool-maintenance">Pool Maintenance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="serviceDescription" className="text-sm sm:text-base">
                  Service Description *
                </Label>
                <Textarea
                  id="serviceDescription"
                  placeholder="Describe the services you provide, your specialties, and what makes your business unique..."
                  rows={4}
                  required
                  maxLength={500}
                  value={formData.serviceDescription}
                  onChange={(e) => setFormData({ ...formData, serviceDescription: e.target.value })}
                  className="text-sm sm:text-base resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  {formData.serviceDescription.length}/500 characters
                </p>
              </div>
            </div>

            {/* Years in Business */}
            <div className="space-y-2">
              <Label htmlFor="yearsInBusiness" className="text-sm sm:text-base">
                Years in Business *
              </Label>
              <Select
                value={formData.yearsInBusiness}
                onValueChange={(value) => setFormData({ ...formData, yearsInBusiness: value })}
              >
                <SelectTrigger id="yearsInBusiness" className="text-sm sm:text-base h-10 sm:h-11">
                  <SelectValue placeholder="Select experience level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="less-than-1">Less than 1 year</SelectItem>
                  <SelectItem value="1-3">1-3 years</SelectItem>
                  <SelectItem value="3-5">3-5 years</SelectItem>
                  <SelectItem value="5-10">5-10 years</SelectItem>
                  <SelectItem value="10-plus">10+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Preferred Pricing Plan */}
            <div className="space-y-2">
              <Label htmlFor="preferredPlan" className="text-sm sm:text-base">
                Preferred Pricing Plan *
              </Label>
              <Select
                value={formData.preferredPlan}
                onValueChange={(value) => setFormData({ ...formData, preferredPlan: value })}
              >
                <SelectTrigger id="preferredPlan" className="text-sm sm:text-base h-10 sm:h-11">
                  <SelectValue placeholder="Select your preferred plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">{vendorMonthlyFee}/month (Month-to-Month)</SelectItem>
                  <SelectItem value="annual">
                    {vendorAnnualFee}/year (Annual Prepaid - Save $
                    {parseInt(vendorMonthlyFee.replace('$', '')) * 12 -
                      parseInt(vendorAnnualFee.replace('$', ''))}
                    )
                  </SelectItem>
                  <SelectItem value="undecided">Not Sure Yet</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                You can change this decision during onboarding. This helps us understand demand.
              </p>
            </div>

            {/* Additional Information */}
            <div className="space-y-2">
              <Label htmlFor="additionalInfo" className="text-sm sm:text-base">
                Additional Information (Optional)
              </Label>
              <Textarea
                id="additionalInfo"
                placeholder="Awards, certifications, special qualifications, or anything else you'd like us to know..."
                rows={3}
                maxLength={300}
                value={formData.additionalInfo}
                onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                className="text-sm sm:text-base resize-none"
              />
              <p className="text-xs text-muted-foreground">
                {formData.additionalInfo?.length || 0}/300 characters
              </p>
            </div>

            {/* Pricing Reminder */}
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground border">
              <p className="flex items-start gap-2">
                <span className="font-semibold text-foreground">Pricing:</span>
                <span>
                  {vendorMonthlyFee}/month (month-to-month) or {vendorAnnualFee}/year prepaid after
                  approval. Includes enterprise-grade KYC/KYB verification and platform access.
                  First month is non-refundable once verification is completed. No free trial.
                  Cancel anytime to stop future monthly renewals. Annual prepayment is
                  non-refundable.
                </span>
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              aria-label={isSubmitting ? 'Joining waitlist' : 'Join vendor waitlist'}
              className="w-full h-15 sm:h-10 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors font-semibold text-base sm:text-lg md:text-xl flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Joining Waitlist...</span>
                </>
              ) : (
                <span>Join Vendor Waitlist</span>
              )}
            </button>

            <p className="text-xs text-center text-muted-foreground">
              By submitting this form, you'll be added to the vendor waitlist. We'll contact you
              when the platform launches so you can complete the official onboarding process.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
